import React, { useState, useEffect, useCallback } from 'react';
import { Card, InputNumber, Row, Col, Typography, Divider, Button, Tag, Spin, App } from 'antd';
import { RiseOutlined, CalendarOutlined, DollarOutlined, CloudDownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { fetchProfitForecast } from '../../shared/api/client';
import { FISCAL_MONTH_I18N_KEYS, calendarMonthIndex, fiscalMonthKey } from '../../shared/lib';
import { useAppColors } from '../../shared/theme';

interface SalesGrowth {
  year: number;
  annualGrowth: number;
  monthlyGrowth: number;
}

interface MonthlyGrowth2025 {
  month: number;
  monthName: string;
  growth: number;
  fromKeepa?: boolean;
}

interface TACoS {
  month: number;
  tacos: number;
}

export interface SalesPlanData {
  salesGrowth: SalesGrowth[];
  monthlyGrowth2025: MonthlyGrowth2025[];
  tacos: TACoS[];
  targetTacos: number;
}

interface SalesPlanProps {
  onDataChange?: (data: SalesPlanData) => void;
  asin?: string;
  competitorAsins?: string[];
  purchasePrice?: number;
  initialData?: Partial<SalesPlanData>;
}

const SalesPlan: React.FC<SalesPlanProps> = ({ onDataChange, asin, competitorAsins, purchasePrice, initialData }) => {
  const { t } = useTranslation();
  const c = useAppColors();
  const { message } = App.useApp();
  const [seasonalityLoading, setSeasonalityLoading] = useState(false);

  // Track if user has made changes (dirty flag to prevent unwanted resets)
  const isDirtyRef = React.useRef(false);
  // Track the last applied initialData to avoid re-applying same data
  const appliedInitialRef = React.useRef<string | null>(null);

  const [salesGrowth, setSalesGrowth] = useState<SalesGrowth[]>([
    { year: 2026, annualGrowth: 40, monthlyGrowth: 2.8 },
    { year: 2027, annualGrowth: 35, monthlyGrowth: 2.5 },
    { year: 2028, annualGrowth: 30, monthlyGrowth: 2.2 },
    { year: 2029, annualGrowth: 25, monthlyGrowth: 1.9 },
    { year: 2030, annualGrowth: 20, monthlyGrowth: 1.5 },
  ]);

  // monthName хранит канонический английский ключ ('july', 'august', ...).
  // UI отображает имя через i18n по индексу month-1, payload остаётся
  // язык-нейтральным — это нужно для XLSX-экспорта и шеринга расчётов.
  const [monthlyGrowth2025, setMonthlyGrowth2025] = useState<MonthlyGrowth2025[]>([
    { month: 1, monthName: fiscalMonthKey(1), growth: 0 },
    { month: 2, monthName: fiscalMonthKey(2), growth: 5 },
    { month: 3, monthName: fiscalMonthKey(3), growth: 8 },
    { month: 4, monthName: fiscalMonthKey(4), growth: 3 },
    { month: 5, monthName: fiscalMonthKey(5), growth: 4 },
    { month: 6, monthName: fiscalMonthKey(6), growth: 6 },
    { month: 7, monthName: fiscalMonthKey(7), growth: 2 },
    { month: 8, monthName: fiscalMonthKey(8), growth: 3 },
    { month: 9, monthName: fiscalMonthKey(9), growth: 4 },
    { month: 10, monthName: fiscalMonthKey(10), growth: 3 },
    { month: 11, monthName: fiscalMonthKey(11), growth: 2 },
    { month: 12, monthName: fiscalMonthKey(12), growth: 4 },
  ]);

  const [tacos, setTacos] = useState<TACoS[]>([
    { month: 1, tacos: 55 },
    { month: 2, tacos: 45 },
    { month: 3, tacos: 35 },
    { month: 4, tacos: 25 },
    { month: 5, tacos: 20 },
  ]);

  const [targetTacos, setTargetTacos] = useState(17);

  // Notify parent of data changes (use stable callback reference)
  const onDataChangeRef = React.useRef(onDataChange);
  onDataChangeRef.current = onDataChange;

  useEffect(() => {
    onDataChangeRef.current?.({ salesGrowth, monthlyGrowth2025, tacos, targetTacos });
  }, [salesGrowth, monthlyGrowth2025, tacos, targetTacos]);

  useEffect(() => {
    // Skip if user has made edits
    if (isDirtyRef.current) return;
    if (!initialData) return;

    // Skip if this is the same data we already applied (compare by value)
    const serialized = JSON.stringify(initialData);
    if (appliedInitialRef.current === serialized) return;

    appliedInitialRef.current = serialized;
    if (initialData.salesGrowth) setSalesGrowth(initialData.salesGrowth);
    if (initialData.monthlyGrowth2025) setMonthlyGrowth2025(initialData.monthlyGrowth2025);
    if (initialData.tacos) setTacos(initialData.tacos);
    if (initialData.targetTacos != null) setTargetTacos(initialData.targetTacos);
  }, [initialData]);

  const loadSeasonality = useCallback(async () => {
    if (!asin) return;
    setSeasonalityLoading(true);
    try {
      const res = await fetchProfitForecast({
        asin,
        competitor_asins: competitorAsins,
        storage_fee_per_unit: 0,
        purchase_price: purchasePrice ?? 0,
      });
      if (res.seasonality && res.seasonality.length === 12) {
        // Сезонность с бэкенда календарная (0 = январь), таблица — фискальная (1 = июль).
        const seasonality = res.seasonality;
        setMonthlyGrowth2025((prev) =>
          prev.map((item) => ({
            ...item,
            growth: +seasonality[calendarMonthIndex(item.month)].toFixed(1),
            fromKeepa: true,
          })),
        );
        message.success(t('idea_calc.sales_plan.msg_seasonality_loaded'));
      } else {
        message.info(t('idea_calc.sales_plan.msg_no_seasonality'));
      }
    } catch {
      message.error(t('idea_calc.sales_plan.msg_seasonality_error'));
    } finally {
      setSeasonalityLoading(false);
    }
  }, [asin, competitorAsins, purchasePrice]);

  const updateSalesGrowth = (year: number, field: 'annualGrowth' | 'monthlyGrowth', value: number) => {
    isDirtyRef.current = true;
    setSalesGrowth((prev) =>
      prev.map((item) => {
        if (item.year === year) {
          if (field === 'annualGrowth') {
            const mg = (Math.pow(1 + value / 100, 1 / 12) - 1) * 100;
            return { ...item, annualGrowth: value, monthlyGrowth: mg };
          }
          return { ...item, [field]: value };
        }
        return item;
      }),
    );
  };

  const updateMonthlyGrowth2025 = (month: number, value: number) => {
    isDirtyRef.current = true;
    setMonthlyGrowth2025((prev) =>
      prev.map((item) => (item.month === month ? { ...item, growth: value, fromKeepa: false } : item)),
    );
  };

  const updateTacos = (month: number, value: number) => {
    isDirtyRef.current = true;
    setTacos((prev) =>
      prev.map((item) => (item.month === month ? { ...item, tacos: value } : item)),
    );
  };

  const handleTargetTacosChange = (value: number | null) => {
    isDirtyRef.current = true;
    setTargetTacos(value ?? 0);
  };

  const sectionStyle: React.CSSProperties = {
    background: c.fillSubtle,
    borderRadius: 8,
    padding: 16,
    border: `1px solid ${c.borderSubtle}`,
  };

  return (
    <Card title={t('idea_calc.sales_plan.title')} variant="outlined">
      <Row gutter={[24, 24]} style={{ display: 'flex', flexWrap: 'wrap' }}>
        <Col xs={24} lg={8} style={{ display: 'flex' }}>
          <div style={{ ...sectionStyle, flex: 1 }}>
            <Typography.Text strong>
              <RiseOutlined style={{ color: c.success, marginRight: 8 }} />
              {t('idea_calc.sales_plan.section_growth')}
            </Typography.Text>
            <Row gutter={8} style={{ marginTop: 12, marginBottom: 4 }}>
              <Col span={8}><Typography.Text type="secondary" style={{ fontSize: 14 }}>{t('idea_calc.sales_plan.col_year')}</Typography.Text></Col>
              <Col span={8}><Typography.Text type="secondary" style={{ fontSize: 14 }}>{t('idea_calc.sales_plan.col_annual_pct')}</Typography.Text></Col>
              <Col span={8}><Typography.Text type="secondary" style={{ fontSize: 14 }}>{t('idea_calc.sales_plan.col_monthly_pct')}</Typography.Text></Col>
            </Row>
            {salesGrowth.map((item) => (
              <Row gutter={8} key={item.year} align="middle" style={{ marginBottom: 6 }}>
                <Col span={8}><Typography.Text>{item.year}</Typography.Text></Col>
                <Col span={8}>
                  <InputNumber
                    size="small"
                    min={0}
                    max={1000}
                    step={0.1}
                    value={item.annualGrowth}
                    onChange={(v) => updateSalesGrowth(item.year, 'annualGrowth', v ?? 0)}
                    style={{ width: '100%' }}
                    suffix="%"
                  />
                </Col>
                <Col span={8}>
                  <InputNumber
                    size="small"
                    readOnly
                    value={+item.monthlyGrowth.toFixed(1)}
                    style={{ width: '100%', background: c.fillMuted }}
                    suffix="%"
                  />
                </Col>
              </Row>
            ))}
          </div>
        </Col>

        <Col xs={24} lg={8} style={{ display: 'flex' }}>
          <div style={{ ...sectionStyle, flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <Typography.Text strong>
                <CalendarOutlined style={{ color: c.info, marginRight: 8 }} />
                {t('idea_calc.sales_plan.section_monthly_growth')}
              </Typography.Text>
              {asin && (
                <Button
                  size="small"
                  type="link"
                  icon={seasonalityLoading ? <Spin size="small" /> : <CloudDownloadOutlined />}
                  onClick={loadSeasonality}
                  disabled={seasonalityLoading}
                >
                  Keepa
                </Button>
              )}
            </div>
            <Row gutter={[8, 4]}>
              {monthlyGrowth2025.map((item) => (
                <Col span={12} key={item.month}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 50, fontSize: 14 }}>
                      <Typography.Text style={{ fontSize: 14 }}>{t(FISCAL_MONTH_I18N_KEYS[item.month - 1] || 'idea_calc.months.january')}</Typography.Text>
                      {item.fromKeepa && <Tag color="blue" style={{ fontSize: 12, lineHeight: '14px', padding: '0 3px', margin: 0 }}>K</Tag>}
                    </span>
                    <InputNumber
                      size="small"
                      min={-100}
                      max={100}
                      step={0.1}
                      value={item.growth}
                      onChange={(v) => updateMonthlyGrowth2025(item.month, v ?? 0)}
                      style={{ flex: 1 }}
                      suffix="%"
                    />
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </Col>

        <Col xs={24} lg={8} style={{ display: 'flex' }}>
          <div style={{ ...sectionStyle, flex: 1 }}>
            <Typography.Text strong>
              <DollarOutlined style={{ color: c.warning, marginRight: 8 }} />
              {t('idea_calc.sales_plan.section_tacos')}
            </Typography.Text>
            <Row gutter={8} style={{ marginTop: 12, marginBottom: 4 }}>
              <Col span={12}><Typography.Text type="secondary" style={{ fontSize: 14 }}>{t('idea_calc.sales_plan.col_month')}</Typography.Text></Col>
              <Col span={12}><Typography.Text type="secondary" style={{ fontSize: 14 }}>{t('idea_calc.sales_plan.col_tacos_pct')}</Typography.Text></Col>
            </Row>
            {tacos.map((item) => (
              <Row gutter={8} key={item.month} align="middle" style={{ marginBottom: 6 }}>
                <Col span={12}><Typography.Text>{t('idea_calc.sales_plan.month_label', { n: item.month })}</Typography.Text></Col>
                <Col span={12}>
                  <InputNumber
                    size="small"
                    min={0}
                    max={100}
                    step={0.1}
                    value={item.tacos}
                    onChange={(v) => updateTacos(item.month, v ?? 0)}
                    style={{ width: '100%' }}
                    suffix="%"
                  />
                </Col>
              </Row>
            ))}
            <Divider style={{ margin: '12px 0' }} />
            <Row gutter={8} align="middle">
              <Col span={12}><Typography.Text strong>{t('idea_calc.sales_plan.target_tacos')}</Typography.Text></Col>
              <Col span={12}>
                <InputNumber
                  size="small"
                  min={0}
                  max={100}
                  step={0.1}
                  value={targetTacos}
                  onChange={handleTargetTacosChange}
                  style={{ width: '100%' }}
                  suffix="%"
                />
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </Card>
  );
};

export default React.memo(SalesPlan);
